import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Input from '../../components/ui/Input';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import { supplierApplicationService, supplierDocumentService } from '../../services/supplierApplicationService';
import { supabase } from '../../lib/supabase';
import { FullWidthSection, ContentWrapper } from '../../components/ui/Layout';
import { useLanguage } from '../../contexts/LanguageContext';

const SupplierRegistration = () => {
  const [searchParams] = useSearchParams();
  const initialApplicationId = searchParams?.get('applicationId');
  const initialStep = parseInt(searchParams?.get('step')) || 1;

  const { language } = useLanguage();
  const [currentStep, setCurrentStep] = useState(initialStep);
  const [countries, setCountries] = useState([]);
  const [formData, setFormData] = useState({
    businessName: '',
    contactPerson: '',
    email: '',
    phone: '',
    physicalAddress: '',
    countryId: '',
    yearsInOperation: '',
    businessRegistrationNumber: '',
    supplierType: 'cooperative'
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [registrationSuccess, setRegistrationSuccess] = useState(false);
  const [applicationId, setApplicationId] = useState(initialApplicationId);

  // Document Upload State
  const [documents, setDocuments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({});
  const [dragActive, setDragActive] = useState(null);

  const navigate = useNavigate();

  useEffect(() => {
    loadCountries();

    if (initialApplicationId) {
      loadExistingApplication();
    }
  }, [initialApplicationId]);

  const loadExistingApplication = async () => {
    try {
      const appData = await supplierApplicationService?.getApplicationById(initialApplicationId);
      if (appData) {
        setFormData(prev => ({
          ...prev,
          businessName: appData?.businessName || '',
          contactPerson: appData?.contactPerson || '',
          email: appData?.email || '',
          phone: appData?.phone || '',
          physicalAddress: appData?.physicalAddress || '',
          countryId: appData?.countryId || '',
          yearsInOperation: appData?.yearsInOperation?.toString() || '',
          businessRegistrationNumber: appData?.businessRegistrationNumber || '',
          supplierType: appData?.supplierType || 'cooperative'
        }));

        const docs = await supplierDocumentService?.getDocumentsByApplication(initialApplicationId);
        setDocuments(docs || []);
      }
    } catch (error) {
      console.error('Failed to load existing application:', error);
    }
  };

  const loadCountries = async () => {
    try {
      const { data, error } = await supabase?.from('countries')?.select('*')?.eq('is_active', true)?.order('name_en');
      if (!error) setCountries(data || []);
    } catch (error) {
      console.error('Failed to load countries:', error);
    }
  };

  const content = {
    en: {
      title: 'Supplier Registration',
      subtitle: 'Join the WGIH platform as an African coffee supplier',
      step1: 'Basic Information',
      step2: 'Business Details',
      step3: 'Document Upload',
      businessName: 'Business Name',
      contactPerson: 'Contact Person',
      email: 'Email Address',
      phone: 'Phone Number',
      physicalAddress: 'Physical Address',
      country: 'Country',
      selectCountry: 'Select your country',
      yearsInOperation: 'Years in Operation',
      businessRegistration: 'Business Registration Number',
      supplierType: 'Supplier Type',
      individualFarmer: 'Individual Farmer',
      cooperative: 'Cooperative',
      exportCompany: 'Export Company',
      next: 'Next',
      previous: 'Previous',
      submit: 'Submit Application',
      submitting: 'Submitting...',
      successTitle: 'Application Submitted!',
      successMessage: 'Your supplier application has been submitted successfully. Please proceed to upload required documents.',
      uploadDocuments: 'Upload Documents',
      termsAccept: 'I accept the terms of service and supplier obligations',
      progressLabel: 'Application Progress',
      // Document Upload Translations
      docTitle: 'Document Upload',
      docSubtitle: 'Upload required credentials and certifications for verification',
      businessLicense: 'Business License',
      businessLicenseDesc: 'Official business registration certificate',
      exportPermit: 'Export Permit',
      exportPermitDesc: 'Coffee export license from your country',
      organicCert: 'Organic Certification',
      organicCertDesc: 'USDA Organic or equivalent certification',
      fairTradeCert: 'Fair Trade Certification',
      fairTradeCertDesc: 'Fair Trade certification (optional)',
      specialtyCert: 'Specialty Certification',
      specialtyCertDesc: 'SCA or specialty coffee certification (optional)',
      taxRegistration: 'Tax Registration',
      taxRegistrationDesc: 'Tax identification document',
      bankVerification: 'Bank Verification',
      bankVerificationDesc: 'Bank account verification letter',
      qualityReport: 'Quality Test Report',
      qualityReportDesc: 'Coffee quality analysis report (optional)',
      dragDrop: 'Drag & drop file here or click to browse',
      acceptedFormats: 'Accepted: PDF, JPG, PNG (Max 10MB)',
      uploading: 'Uploading...',
      uploaded: 'Uploaded',
      required: 'Required',
      optional: 'Optional',
      delete: 'Delete',
      docSubmit: 'Submit for Review',
      docSubmitting: 'Submitting...',
      progressTitle: 'Upload Progress',
      documentsUploaded: 'documents uploaded',
      requiredDocs: 'Required documents',
      successTitle: 'Application Submitted!',
      successMessage: 'Your supplier application and documents have been submitted successfully. Our team will review your application and contact you soon.',
      goToDashboard: 'Back to Home'
    },
    zh: {
      title: '供应商注册',
      subtitle: '作为非洲咖啡供应商加入WGIH平台',
      step1: '基本信息',
      step2: '业务详情',
      step3: '文件上传',
      businessName: '企业名称',
      contactPerson: '联系人',
      email: '电子邮件地址',
      phone: '电话号码',
      physicalAddress: '实际地址',
      country: '国家',
      selectCountry: '选择您的国家',
      yearsInOperation: '运营年限',
      businessRegistration: '企业注册号',
      supplierType: '供应商类型',
      individualFarmer: '个体农民',
      cooperative: '合作社',
      exportCompany: '出口公司',
      next: '下一步',
      previous: '上一步',
      submit: '下一步',
      submitting: '提交中...',
      progressLabel: '申请进度',
      // Document Upload Translations (ZH)
      docTitle: '文件上传',
      docSubtitle: '上传所需的凭证和认证以进行验证',
      businessLicense: '营业执照',
      businessLicenseDesc: '官方企业注册证书',
      exportPermit: '出口许可证',
      exportPermitDesc: '您所在国家的咖啡出口许可证',
      organicCert: '有机认证',
      organicCertDesc: 'USDA有机或同等认证',
      fairTradeCert: '公平贸易认证',
      fairTradeCertDesc: '公平贸易认证（可选）',
      specialtyCert: '精品认证',
      specialtyCertDesc: 'SCA或精品咖啡认证（可选）',
      taxRegistration: '税务登记',
      taxRegistrationDesc: '税务识别文件',
      bankVerification: '银行验证',
      bankVerificationDesc: '银行账户验证信',
      qualityReport: '质量检测报告',
      qualityReportDesc: '咖啡质量分析报告（可选）',
      dragDrop: '拖放文件到此处或点击浏览',
      acceptedFormats: '接受：PDF、JPG、PNG（最大10MB）',
      uploading: '上传中...',
      uploaded: '已上传',
      required: '必需',
      optional: '可选',
      delete: '删除',
      docSubmit: '提交审核',
      docSubmitting: '提交中...',
      progressTitle: '上传进度',
      documentsUploaded: '个文件已上传',
      requiredDocs: '必需文件',
      successTitle: '申请已提交！',
      successMessage: '您的供应商申请和文件已成功提交。我们的团队将审核您的申请并尽快与您联系。',
      goToDashboard: '前往仪表板'
    }
  };

  const t = content?.[language];

  const steps = [
    { number: 1, label: t?.step1 },
    { number: 2, label: t?.step2 },
    { number: 3, label: t?.step3 }
  ];

  const handleChange = (e) => {
    const { name, value } = e?.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors?.[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateStep = (step) => {
    const newErrors = {};
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const phoneRegex = /^\+?[0-9]{10,15}$/;

    if (step === 1) {
      if (!formData?.businessName?.trim()) {
        newErrors.businessName = language === 'en' ? 'Business name is required' : '企业名称为必填项';
      }
      if (!formData?.contactPerson?.trim()) {
        newErrors.contactPerson = language === 'en' ? 'Contact person is required' : '联系人为必填项';
      }
      if (!formData?.email?.trim()) {
        newErrors.email = language === 'en' ? 'Email is required' : '电子邮件为必填项';
      } else if (!emailRegex?.test(formData?.email)) {
        newErrors.email = language === 'en' ? 'Invalid email format' : '电子邮件格式无效';
      }
      if (!formData?.phone?.trim()) {
        newErrors.phone = language === 'en' ? 'Phone number is required' : '电话号码为必填项';
      } else if (!phoneRegex?.test(formData?.phone)) {
        newErrors.phone = language === 'en' ? 'Invalid phone format' : '电话号码格式无效';
      }
    }

    if (step === 2) {
      if (!formData?.physicalAddress?.trim()) {
        newErrors.physicalAddress = language === 'en' ? 'Physical address is required' : '实际地址为必填项';
      }
      if (!formData?.countryId) {
        newErrors.countryId = language === 'en' ? 'Country is required' : '国家为必填项';
      }
      if (!formData?.yearsInOperation || formData?.yearsInOperation < 0) {
        newErrors.yearsInOperation = language === 'en' ? 'Years in operation is required' : '运营年限为必填项';
      }
      if (!formData?.businessRegistrationNumber?.trim()) {
        newErrors.businessRegistrationNumber = language === 'en' ? 'Business registration number is required' : '企业注册号为必填项';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const documentTypes = [
    { type: 'business_license', label: t?.businessLicense, description: t?.businessLicenseDesc, required: true, icon: 'FileText' },
    { type: 'export_permit', label: t?.exportPermit, description: t?.exportPermitDesc, required: true, icon: 'FileCheck' },
    { type: 'organic_certification', label: t?.organicCert, description: t?.organicCertDesc, required: true, icon: 'Award' },
    { type: 'fair_trade_certification', label: t?.fairTradeCert, description: t?.fairTradeCertDesc, required: false, icon: 'Award' },
    { type: 'specialty_certification', label: t?.specialtyCert, description: t?.specialtyCertDesc, required: false, icon: 'Award' },
    { type: 'tax_registration', label: t?.taxRegistration, description: t?.taxRegistrationDesc, required: false, icon: 'FileText' },
    { type: 'bank_verification', label: t?.bankVerification, description: t?.bankVerificationDesc, required: false, icon: 'Building2' },
    { type: 'quality_test_report', label: t?.qualityReport, description: t?.qualityReportDesc, required: false, icon: 'ClipboardCheck' }
  ];

  const handleNext = async () => {
    if (validateStep(currentStep)) {
      if (currentStep === 2) {
        // Create application when moving from step 2 to 3
        await handleCreateApplication();
      } else {
        setCurrentStep(prev => prev + 1);
      }
    }
  };

  const handleCreateApplication = async () => {
    setIsSubmitting(true);
    try {
      const application = await supplierApplicationService?.createApplication({
        businessName: formData?.businessName,
        contactPerson: formData?.contactPerson,
        email: formData?.email,
        phone: formData?.phone,
        physicalAddress: formData?.physicalAddress,
        countryId: formData?.countryId,
        yearsInOperation: parseInt(formData?.yearsInOperation),
        businessRegistrationNumber: formData?.businessRegistrationNumber,
        supplierType: formData?.supplierType
      });

      setApplicationId(application?.id);
      setCurrentStep(3);
    } catch (error) {
      setErrors({ submit: error?.message || (language === 'en' ? 'Submission failed' : '提交失败') });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handlePrevious = () => {
    setCurrentStep(prev => prev - 1);
  };

  const handleDrag = (e, docType) => {
    e?.preventDefault();
    e?.stopPropagation();
    if (e?.type === 'dragenter' || e?.type === 'dragover') {
      setDragActive(docType);
    } else if (e?.type === 'dragleave') {
      setDragActive(null);
    }
  };

  const handleDrop = async (e, docType) => {
    e?.preventDefault();
    e?.stopPropagation();
    setDragActive(null);

    const file = e?.dataTransfer?.files?.[0];
    if (file) {
      await handleFileUpload(file, docType);
    }
  };

  const handleFileSelect = async (e, docType) => {
    const file = e?.target?.files?.[0];
    if (file) {
      await handleFileUpload(file, docType);
    }
  };

  const handleFileUpload = async (file, docType) => {
    // Validate file
    const maxSize = 10 * 1024 * 1024; // 10MB
    const allowedTypes = ['application/pdf', 'image/jpeg', 'image/png'];

    if (file?.size > maxSize) {
      setErrors({ [docType]: language === 'en' ? 'File size exceeds 10MB' : '文件大小超过10MB' });
      return;
    }

    if (!allowedTypes?.includes(file?.type)) {
      setErrors({ [docType]: language === 'en' ? 'Invalid file type. Use PDF, JPG, or PNG' : '无效的文件类型。使用PDF、JPG或PNG' });
      return;
    }

    try {
      setUploading(true);
      setUploadProgress({ [docType]: 0 });
      setErrors({ [docType]: '' });

      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      // Simulate progress
      const progressInterval = setInterval(() => {
        setUploadProgress(prev => ({
          ...prev,
          [docType]: Math.min((prev?.[docType] || 0) + 10, 90)
        }));
      }, 200);

      // Upload file
      const filePath = await supplierDocumentService?.uploadDocument(file, user?.id, applicationId);

      clearInterval(progressInterval);
      setUploadProgress({ [docType]: 100 });

      // Create document record
      const fileType = file?.type?.split('/')?.[1];
      const docTypeConfig = documentTypes?.find(dt => dt?.type === docType);

      const newDoc = await supplierDocumentService?.createDocumentRecord({
        applicationId: applicationId,
        documentType: docType,
        documentName: file?.name,
        fileUrl: filePath,
        fileType: fileType,
        fileSizeBytes: file?.size,
        isRequired: docTypeConfig?.required || false,
        verificationStatus: 'pending'
      });

      setDocuments(prev => [...prev, newDoc]);

      setTimeout(() => {
        setUploadProgress(prev => ({ ...prev, [docType]: null }));
      }, 1000);
    } catch (error) {
      if (progressInterval) clearInterval(progressInterval);
      setErrors({ [docType]: error?.message || (language === 'en' ? 'Upload failed' : '上传失败') });
      setUploadProgress(prev => ({ ...prev, [docType]: null }));
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (documentId, filePath, docType) => {
    if (!window.confirm(language === 'en' ? 'Delete this document?' : '删除此文件？')) return;

    try {
      await supplierDocumentService?.deleteDocument(documentId, filePath);
      setDocuments(prev => prev?.filter(doc => doc?.id !== documentId));
    } catch (error) {
      alert(error?.message || (language === 'en' ? 'Delete failed' : '删除失败'));
    }
  };

  const getDocumentForType = (docType) => {
    return documents?.find(doc => doc?.documentType === docType);
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();

    // Check if required documents are uploaded
    const requiredDocs = documentTypes.filter(dt => dt.required);
    const uploadedDocs = requiredDocs.filter(dt => getDocumentForType(dt.type));

    if (uploadedDocs.length < requiredDocs.length) {
      setErrors({ submit: language === 'en' ? 'Please upload all required documents' : '请上传所有必需文件' });
      return;
    }

    setIsSubmitting(true);

    try {
      await supplierApplicationService?.updateApplicationStatus(applicationId, 'under_review');
      setRegistrationSuccess(true);
    } catch (error) {
      setErrors({ submit: error?.message || (language === 'en' ? 'Submission failed. Please try again.' : '提交失败。请重试。') });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (registrationSuccess) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <main className="pt-[60px]">
          <FullWidthSection py="py-16">
            <ContentWrapper maxWidth="md">
              <div className="bg-card rounded-lg shadow-card border border-border p-8 text-center">
                <div className="w-16 h-16 bg-success/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Icon name="CheckCircle" size={32} color="var(--color-success)" />
                </div>
                <h2 className="text-2xl font-heading font-bold text-foreground mb-2">
                  {t?.successTitle}
                </h2>
                <p className="text-muted-foreground mb-6">
                  {t?.successMessage}
                </p>
                <Button
                  onClick={() => navigate('/')}
                  className="w-full"
                  iconName="Home"
                  iconPosition="right"
                >
                  {t?.goToDashboard}
                </Button>
              </div>
            </ContentWrapper>
          </FullWidthSection>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="pt-[60px]">
        <FullWidthSection padding="" py="py-8">
          <ContentWrapper maxWidth="5xl" padding="px-5">
            <div className="bg-card rounded-lg shadow-card border border-border p-8">
              <div className="text-center mb-8">
                <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Icon name="Building2" size={32} color="var(--color-primary)" />
                </div>
                <h1 className="text-3xl font-heading font-bold text-foreground mb-2">
                  {t?.title}
                </h1>
                <p className="text-muted-foreground">{t?.subtitle}</p>
              </div>

              {/* Progress Indicator */}
              <div className="mb-8">
                <div className="flex items-center justify-between mb-2">
                  {steps?.map((step, index) => (
                    <React.Fragment key={step?.number}>
                      <div className="flex flex-col items-center flex-1">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold transition-smooth ${currentStep >= step?.number
                          ? 'bg-primary text-white' : 'bg-muted text-muted-foreground'
                          }`}>
                          {currentStep > step?.number ? (
                            <Icon name="Check" size={20} />
                          ) : (
                            step?.number
                          )}
                        </div>
                        <span className="text-xs mt-2 text-center text-muted-foreground">{step?.label}</span>
                      </div>
                      {index < steps?.length - 1 && (
                        <div className={`h-1 flex-1 mx-2 rounded transition-smooth ${currentStep > step?.number ? 'bg-primary' : 'bg-muted'
                          }`} />
                      )}
                    </React.Fragment>
                  ))}
                </div>
              </div>

              <form onSubmit={handleSubmit}>
                {/* Step 1: Basic Information */}
                {currentStep === 1 && (
                  <div className="space-y-4">
                    <Input
                      label={t?.businessName}
                      name="businessName"
                      value={formData?.businessName}
                      onChange={handleChange}
                      error={errors?.businessName}
                      required
                    />
                    <Input
                      label={t?.contactPerson}
                      name="contactPerson"
                      value={formData?.contactPerson}
                      onChange={handleChange}
                      error={errors?.contactPerson}
                      required
                    />
                    <Input
                      label={t?.email}
                      type="email"
                      name="email"
                      value={formData?.email}
                      onChange={handleChange}
                      error={errors?.email}
                      required
                    />
                    <Input
                      label={t?.phone}
                      type="tel"
                      name="phone"
                      value={formData?.phone}
                      onChange={handleChange}
                      error={errors?.phone}
                      placeholder="+251911234567"
                      required
                    />
                  </div>
                )}

                {/* Step 2: Business Details */}
                {currentStep === 2 && (
                  <div className="space-y-4">
                    <div>
                      <label className="text-sm font-medium text-foreground mb-2 block">
                        {t?.physicalAddress} <span className="text-red-500">*</span>
                      </label>
                      <textarea
                        name="physicalAddress"
                        value={formData?.physicalAddress}
                        onChange={handleChange}
                        rows={3}
                        className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                      />
                      {errors?.physicalAddress && (
                        <p className="text-sm text-red-500 mt-1">{errors?.physicalAddress}</p>
                      )}
                    </div>
                    <div>
                      <label className="text-sm font-medium text-foreground mb-2 block">
                        {t?.country} <span className="text-red-500">*</span>
                      </label>
                      <select
                        name="countryId"
                        value={formData?.countryId}
                        onChange={handleChange}
                        className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                      >
                        <option value="">{t?.selectCountry}</option>
                        {countries?.map((country) => (
                          <option key={country?.id} value={country?.id}>
                            {language === 'en' ? country?.name_en : country?.name_zh}
                          </option>
                        ))}
                      </select>
                      {errors?.countryId && (
                        <p className="text-sm text-red-500 mt-1">{errors?.countryId}</p>
                      )}
                    </div>
                    <Input
                      label={t?.yearsInOperation}
                      type="number"
                      name="yearsInOperation"
                      value={formData?.yearsInOperation}
                      onChange={handleChange}
                      error={errors?.yearsInOperation}
                      min="0"
                      required
                    />
                    <Input
                      label={t?.businessRegistration}
                      name="businessRegistrationNumber"
                      value={formData?.businessRegistrationNumber}
                      onChange={handleChange}
                      error={errors?.businessRegistrationNumber}
                      placeholder="ET-BRN-2009-00123"
                      required
                    />
                    <div>
                      <label className="text-sm font-medium text-foreground mb-2 block">
                        {t?.supplierType}
                      </label>
                      <div className="grid grid-cols-3 gap-3">
                        {['individual_farmer', 'cooperative', 'export_company']?.map((type) => (
                          <button
                            key={type}
                            type="button"
                            onClick={() => setFormData(prev => ({ ...prev, supplierType: type }))}
                            className={`p-3 rounded-md border-2 transition-smooth text-sm ${formData?.supplierType === type
                              ? 'border-primary bg-primary/10 text-primary' : 'border-border bg-background text-foreground hover:border-primary/50'
                              }`}
                          >
                            {type === 'individual_farmer' && t?.individualFarmer}
                            {type === 'cooperative' && t?.cooperative}
                            {type === 'export_company' && t?.exportCompany}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                {/* Step 3: Document Upload */}
                {currentStep === 3 && (
                  <div className="space-y-6">
                    <div className="text-center mb-6">
                      <h3 className="text-xl font-heading font-bold text-foreground mb-1">{t?.docTitle}</h3>
                      <p className="text-sm text-muted-foreground">{t?.docSubtitle}</p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      {documentTypes?.map((docType) => {
                        const uploadedDoc = getDocumentForType(docType?.type);
                        const isUploading = uploadProgress?.[docType?.type] !== null && uploadProgress?.[docType?.type] !== undefined;
                        const progress = uploadProgress?.[docType?.type] || 0;

                        return (
                          <div key={docType?.type} className="bg-background border border-border rounded-lg p-5">
                            <div className="flex items-start justify-between mb-3">
                              <div className="flex items-center gap-2">
                                <Icon name={docType?.icon} size={20} color="var(--color-primary)" />
                                <h3 className="text-sm font-semibold text-foreground">{docType?.label}</h3>
                              </div>
                              <span className={`text-xs px-2 py-1 rounded ${docType?.required ? 'bg-red-100 text-red-600' : 'bg-muted text-muted-foreground'
                                }`}>
                                {docType?.required ? t?.required : t?.optional}
                              </span>
                            </div>
                            <p className="text-xs text-muted-foreground mb-4">{docType?.description}</p>

                            {uploadedDoc ? (
                              <div className="flex items-center justify-between p-3 bg-success/10 border border-success/20 rounded-md">
                                <div className="flex items-center gap-2 flex-1 min-w-0">
                                  <Icon name="CheckCircle" size={18} color="var(--color-success)" />
                                  <span className="text-sm text-foreground truncate">{uploadedDoc?.documentName}</span>
                                </div>
                                <button
                                  type="button"
                                  onClick={() => handleDelete(uploadedDoc?.id, uploadedDoc?.fileUrl, docType?.type)}
                                  className="ml-2 p-1 hover:bg-muted rounded transition-smooth"
                                >
                                  <Icon name="Trash2" size={16} color="var(--color-muted-foreground)" />
                                </button>
                              </div>
                            ) : isUploading ? (
                              <div className="p-3 bg-muted/50 rounded-md">
                                <div className="flex items-center justify-between mb-2">
                                  <span className="text-sm text-foreground">{t?.uploading}</span>
                                  <span className="text-sm text-primary font-semibold">{progress}%</span>
                                </div>
                                <div className="w-full bg-muted rounded-full h-2">
                                  <div className="bg-primary h-2 rounded-full transition-smooth" style={{ width: `${progress}%` }} />
                                </div>
                              </div>
                            ) : (
                              <div
                                onDragEnter={(e) => handleDrag(e, docType?.type)}
                                onDragLeave={(e) => handleDrag(e, docType?.type)}
                                onDragOver={(e) => handleDrag(e, docType?.type)}
                                onDrop={(e) => handleDrop(e, docType?.type)}
                                className={`border-2 border-dashed rounded-md p-4 text-center cursor-pointer transition-smooth ${dragActive === docType?.type ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/50'
                                  }`}
                              >
                                <input
                                  type="file"
                                  id={`file-${docType?.type}`}
                                  accept=".pdf,.jpg,.jpeg,.png"
                                  onChange={(e) => handleFileSelect(e, docType?.type)}
                                  className="hidden"
                                />
                                <label htmlFor={`file-${docType?.type}`} className="cursor-pointer">
                                  <Icon name="Upload" size={24} color="var(--color-muted-foreground)" className="mx-auto mb-2" />
                                  <p className="text-xs text-foreground mb-1">{t?.dragDrop}</p>
                                  <p className="text-xs text-muted-foreground">{t?.acceptedFormats}</p>
                                </label>
                                {errors?.[docType?.type] && (
                                  <p className="text-xs text-red-500 mt-2">{errors?.[docType?.type]}</p>
                                )}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {errors?.submit && (
                  <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-md">
                    <p className="text-sm text-red-600">{errors?.submit}</p>
                  </div>
                )}

                {/* Navigation Buttons */}
                <div className="flex items-center justify-between mt-8">
                  {currentStep > 1 ? (
                    <Button
                      type="button"
                      variant="outline"
                      onClick={handlePrevious}
                      iconName="ChevronLeft"
                      iconPosition="left"
                    >
                      {t?.previous}
                    </Button>
                  ) : (
                    <div />
                  )}
                  {currentStep < 3 ? (
                    <Button
                      type="button"
                      onClick={handleNext}
                      iconName="ChevronRight"
                      iconPosition="right"
                    >
                      {t?.next}
                    </Button>
                  ) : (
                    <Button
                      type="submit"
                      disabled={isSubmitting || uploading}
                      iconName="CheckCircle"
                      iconPosition="right"
                    >
                      {isSubmitting ? t?.docSubmitting : t?.docSubmit}
                    </Button>
                  )}
                </div>
              </form>
            </div>
          </ContentWrapper>
        </FullWidthSection>
      </main>
    </div>
  );
};

export default SupplierRegistration;